import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { requestClassLogDelete, requestClassLogEdit } from '../lib/api';
import type { ClassLogEditChanges, RecentClassRow } from '../lib/api';

// Mentors / coordinators can't change or delete a logged class directly: this
// modal raises an edit (or delete) request, and the log only changes once an
// admin approves it (Admin → Log Edit Requests).

const INPUT_CLS = 'w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-purple-400';

export const ClassLogEditModal: React.FC<{
  row: RecentClassRow;
  mentorId: string;          // who is asking — the mentor / coordinator's own id
  onClose: () => void;
  onDone: () => void;
}> = ({ row, mentorId, onClose, onDone }) => {
  // Revising a pending request starts from what was already asked for.
  const pending = row.editRequest?.status === 'Pending' ? row.editRequest.changes : {};
  const [form, setForm] = useState<Required<ClassLogEditChanges>>({
    date:       pending.date       ?? row.date,
    attendance: pending.attendance ?? row.attendance,
    startTime:  pending.startTime  ?? row.startTime ?? '',
    endTime:    pending.endTime    ?? row.endTime ?? '',
    portion:    pending.portion    ?? row.portion ?? '',
    topic:      pending.topic      ?? row.topic ?? '',
    remark:     pending.remark     ?? row.remark ?? '',
  });
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState<'edit' | 'delete' | null>(null);
  const set = (patch: Partial<ClassLogEditChanges>) => setForm(f => ({ ...f, ...patch }));

  const handleSubmit = async () => {
    if (!reason.trim()) { toast.error('Please say why this class log needs changing'); return; }
    setSaving('edit');
    try {
      // Send every field — the server keeps only the ones that actually changed.
      const res = await requestClassLogEdit(mentorId, row.logRowIndex, form, reason.trim());
      toast.success(res.revised ? 'Edit request updated — waiting for admin approval' : 'Edit request sent to admin for approval');
      onDone();
    } catch (err: any) { toast.error(err.message); }
    finally { setSaving(null); }
  };

  // e.g. a class logged twice or for the wrong student.
  const handleDelete = async () => {
    if (!reason.trim()) { toast.error('Please say why this class log should be deleted'); return; }
    if (!window.confirm(`Ask admin to delete ${row.studentName}'s class on ${row.date}?\n\nIt stays in the log until admin approves.`)) return;
    setSaving('delete');
    try {
      await requestClassLogDelete(mentorId, row.logRowIndex, reason.trim());
      toast.success('Delete request sent to admin for approval');
      onDone();
    } catch (err: any) { toast.error(err.message); }
    finally { setSaving(null); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg z-10 overflow-hidden max-h-[92vh] flex flex-col">
        <div className="px-6 py-4 shrink-0" style={{ background: 'linear-gradient(135deg,#2b2240,#4a3a6a)' }}>
          <p className="text-white font-bold text-base">Edit Class Log — {row.studentName}</p>
          <p className="text-white/60 text-xs mt-0.5">Changes are sent to admin and only apply once approved</p>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Date</label>
              <input type="date" value={form.date} onChange={e => set({ date: e.target.value })} className={INPUT_CLS} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Attendance</label>
              <div className="grid grid-cols-2 gap-2">
                {(['Present', 'Absent'] as const).map(a => (
                  <button key={a} type="button" onClick={() => set({ attendance: a })}
                    className="py-2.5 rounded-xl text-xs font-bold border-2 transition-all"
                    style={form.attendance === a
                      ? { background: a === 'Present' ? '#16a34a' : '#dc2626', color: '#fff', borderColor: 'transparent' }
                      : { background: '#f9fafb', color: '#6b7280', borderColor: '#e5e7eb' }}>
                    {a}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Start Time</label>
              <input type="time" value={form.startTime} onChange={e => set({ startTime: e.target.value })} className={INPUT_CLS} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">End Time</label>
              <input type="time" value={form.endTime} onChange={e => set({ endTime: e.target.value })} className={INPUT_CLS} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Topic / Task</label>
            <input value={form.topic} onChange={e => set({ topic: e.target.value })} className={INPUT_CLS} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Portion</label>
            <input value={form.portion} onChange={e => set({ portion: e.target.value })} className={INPUT_CLS} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Remark</label>
            <textarea value={form.remark} onChange={e => set({ remark: e.target.value })} rows={2} className={INPUT_CLS} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Reason for change *</label>
            <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} autoFocus
              placeholder="e.g. Logged the wrong date" className={INPUT_CLS} />
          </div>
          <div className="flex gap-3 pt-1">
            <button onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button onClick={handleSubmit} disabled={saving !== null}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg,#2b2240,#5a4a6f)' }}>
              {saving === 'edit' ? 'Sending…' : 'Send for Approval'}
            </button>
          </div>
          <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100">
            <p className="text-xs text-gray-400">Logged by mistake, or logged twice?</p>
            <button onClick={handleDelete} disabled={saving !== null}
              className="px-3 py-2 rounded-xl text-xs font-bold border-2 whitespace-nowrap hover:bg-red-50 disabled:opacity-60"
              style={{ borderColor: '#fca5a5', color: '#b91c1c' }}>
              {saving === 'delete' ? 'Sending…' : '🗑 Request deletion'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Entry point from the "Log a Class" pages to where logged classes are edited.
export const EditLoggedClassLink: React.FC<{ to: string; hint?: string }> = ({ to, hint }) => (
  <Link to={to}
    className="flex items-center gap-3 rounded-xl border-2 px-4 py-3 mb-5 hover:bg-purple-50 transition-colors"
    style={{ borderColor: '#ddd6fe', background: '#faf5ff' }}>
    <span className="text-lg">✏️</span>
    <span className="flex-1 min-w-0">
      <span className="block text-sm font-bold" style={{ color: '#2b2240' }}>Edit a logged class</span>
      <span className="block text-xs text-gray-500">{hint ?? 'Fix classes you already logged, one or many at once — admin approves the change'}</span>
    </span>
    <span className="text-gray-400 font-bold">→</span>
  </Link>
);

const CHIP_STYLE = {
  Pending:  { bg: '#fef3c7', color: '#92400e', edit: '⏳ Edit waiting for admin approval',   delete: '🗑 Delete waiting for admin approval' },
  Approved: { bg: '#dcfce7', color: '#15803d', edit: '✓ Edit approved',                    delete: '✓ Delete approved' },
  Rejected: { bg: '#fee2e2', color: '#991b1b', edit: '✕ Edit rejected',                    delete: '✕ Delete rejected' },
};

// Shows the state of the latest edit / delete request on a class log row.
export const EditRequestChip: React.FC<{ req: RecentClassRow['editRequest'] }> = ({ req }) => {
  if (!req) return null;
  const s = CHIP_STYLE[req.status];
  return (
    <span className="inline-block text-[11px] font-semibold px-2.5 py-1 rounded-lg mt-2" style={{ background: s.bg, color: s.color }}>
      {s[req.type ?? 'edit']}{req.status === 'Pending' && req.requestedByName ? ` (by ${req.requestedByName})` : ''}
      {req.status === 'Rejected' && req.reviewNote ? ` — ${req.reviewNote}` : ''}
    </span>
  );
};
